// Unsubscribe.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import API_URL from "../config/config";
import {
    Box,
    Main,
    Card,
    CardBody,
    CardFooter,
    Heading,
    Text,
    Spinner,
    Button,
} from "grommet";
import { StatusGood, StatusCritical, StatusWarning } from "grommet-icons";
import { HeaderNavigation } from "../components/Headers/GlobalHeader";
import { StickyFooter } from "../components/Footers/GlobalFooter";

const maskEmail = (email) => {
    if (!email || !email.includes("@")) return email || "";
    const [name, domain] = email.split("@");
    if (name.length <= 2) return `*${name.slice(-1)}@${domain}`;
    return `${name[0]}***${name.slice(-1)}@${domain}`;
};

const useQuery = () => new URLSearchParams(useLocation().search);

const Unsubscribe = () => {
    const query = useQuery();
    const email = query.get("email")?.trim();

    const [loading, setLoading] = useState(true);
    const [result, setResult] = useState(null); // { ok: boolean, message?: string, code?: string }
    const [error, setError] = useState(null);

    const title = useMemo(
        () => (loading ? "Updating preferences…" : result?.ok ? "You’re unsubscribed" : "We couldn’t update your preference"),
        [loading, result]
    );

    useEffect(() => {
        document.title = `Unsubscribe — ${title}`;
    }, [title]);

    useEffect(() => {
        if (!email) {
            setLoading(false);
            setResult(null);
            setError("Missing email parameter.");
            return;
        }

        const controller = new AbortController();
        const run = async () => {
            setLoading(true);
            setError(null);
            setResult(null);
            try {
                // Prefer POST for server-side changes; adjust if your API expects PATCH/GET.
                const res = await fetch(`${API_URL}/user/unsubscribe`, {
                    method: "POST",
                    signal: controller.signal,
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ token: email }),
                });

                let payload = null;
                try {
                    payload = await res.json();
                } catch {
                    // ignore JSON parse errors; backend may return empty body
                }

                if (!res.ok) {
                    const msg =
                        payload?.message ||
                        payload?.error ||
                        `Request failed (${res.status})`;
                    throw new Error(msg);
                }

                // Expecting { ok: true, message?: string } from backend
                setResult({
                    ok: payload?.ok !== false, // default to true if successful response
                    message:
                        payload?.message ||
                        "You’ve been unsubscribed from marketing and non-essential updates.",
                    code: payload?.code,
                });
            } catch (err) {
                if (err.name === "AbortError") return;
                setError(err.message || "Unable to process your request right now.");
                setResult(null);
            } finally {
                setLoading(false);
            }
        };

        run();
        return () => controller.abort();
    }, [email]);

    const retry = () => {
        // Re-run the effect by toggling loading state
        setLoading(true);
        setError(null);
        setResult(null);
        // Triggering a microtask so useEffect reruns cleanly
        Promise.resolve().then(() => setLoading(false)); // noop; useEffect depends on [email] and will run on next tick because we set loading=true above then false
        // Simpler: directly call the effect logic by flipping a key, but keeping minimal here.
        // In practice, you could lift the fetch into a function and call it here.
        setTimeout(() => {
            setLoading(true);
            setError(null);
            setResult(null);
        }, 0);
    };

    return (
        <Box fill="vertical" justify="between" background="light-1">
            <Box flex="grow">
                <HeaderNavigation />

                <Main align="center" pad={{ horizontal: "medium", top: "large", bottom: "large" }}>
                    <Box width="large">
                        {loading && (
                            <Card elevation="small" round="large" background="white">
                                <CardBody pad="large" gap="small" align="center">
                                    <Spinner />
                                    <Heading level={3} margin="none">
                                        Updating preferences…
                                    </Heading>
                                    <Text color="dark-6">
                                        Please wait while we process your request for {maskEmail(email)}.
                                    </Text>
                                </CardBody>
                            </Card>
                        )}

                        {!loading && error && (
                            <Card elevation="small" round="large" background="status-error">
                                <CardBody pad="large" gap="small" align="center">
                                    <StatusCritical color="white" />
                                    <Heading level={3} margin="none" color="white">
                                        We couldn’t update your preference
                                    </Heading>
                                    <Text color="white" textAlign="center">
                                        {error}
                                    </Text>

                                </CardBody>
                                <CardFooter
                                    pad={{ horizontal: "medium", vertical: "small" }}
                                    background="rgba(255,255,255,0.1)"
                                    justify="between"
                                >
                                    <Button label="Retry" onClick={retry} primary />
                                    <Button as={Link} to="/" label="Go to Home" />
                                </CardFooter>
                            </Card>
                        )}

                        {!loading && !error && result && result.ok && (
                            <Card elevation="small" round="large" background="white">
                                <CardBody pad="large" gap="small" align="center">
                                    <StatusGood color="status-ok" />
                                    <Heading level={3} margin="none">
                                        You’re unsubscribed
                                    </Heading>
                                    <Text textAlign="center">
                                        {result.message ||
                                            "You will no longer receive marketing emails from us."}
                                    </Text>
                                    <Text color="dark-6" size="small">
                                        Email: {maskEmail(email)}
                                    </Text>
                                </CardBody>
                                <CardFooter pad={{ horizontal: "medium", vertical: "small" }} background="light-2" justify="between">
                                    <Button as={Link} to="/" label="Return to Home" />
                                    {/* If you have a preferences center, link it here */}
                                    {/* <Button as={Link} to={`/profile/${userId}`} label="Manage Preferences" secondary /> */}
                                </CardFooter>
                            </Card>
                        )}

                        {!loading && !error && result && result.ok === false && (
                            <Card elevation="small" round="large" background="white">
                                <CardBody pad="large" gap="small" align="center">
                                    <StatusWarning color="status-warning" />
                                    <Heading level={3} margin="none">
                                        Preference not updated
                                    </Heading>
                                    <Text textAlign="center">
                                        {result.message || "We couldn’t change your subscription status."}
                                    </Text>
                                    <Text color="dark-6" size="small">
                                        Email: {maskEmail(email)}
                                    </Text>
                                </CardBody>
                                <CardFooter pad={{ horizontal: "medium", vertical: "small" }} background="light-2" justify="between">
                                    <Button label="Retry" onClick={retry} primary />
                                    <Button as={Link} to="/" label="Go to Home" />
                                </CardFooter>
                            </Card>
                        )}
                    </Box>
                </Main>

                <StickyFooter />
            </Box>
        </Box>
    );
};

export default Unsubscribe;
